import { useIntervalFn } from "@vueuse/core";
import { getNewToken } from "./getRefreshToken";

type TokenState = {
  isAuthenticated: string | null;
  expiresAt: number | null; // epoch ms
  refreshing: boolean;
};
// TODO Use server-provided expiry
export const ACCESS_TOKEN_EXPIRY_SECONDS = 6 * 3600 - 120; // 2 min less than server for safety
const REFRESH_EARLY_SECONDS = 60 * 5; // refresh 5 minutes before expiry

/**
 * Unknown expiry is NOT "expiring soon": refreshing on a guess rotates the session (and revokes
 * the access token in use) for nothing. A really expired token is still handled by the
 * 401 -> refresh path in useFetchApi / useFetchApiWithToken.
 */
export const isTokenExpiringSoon = (expiresAt: number | null, now: number) => {
  if (!expiresAt) return false;
  return expiresAt - now < REFRESH_EARLY_SECONDS * 1000;
};

export function useAuth() {
  const state = useState<TokenState>("auth_state", () => ({
    isAuthenticated: useCookie<string | null>("isAuthenticated").value || null,
    expiresAt: null,
    refreshing: false,
  }));

  const saveExpiry = (expiresInSeconds: number) => {
    const exp = Date.now() + expiresInSeconds * 1000;
    state.value.expiresAt = exp;
    if (import.meta.client) {
      localStorage.setItem("is_authenticated_exp", String(exp));
      // multi-tab signal
      localStorage.setItem("is_authenticated_updated_at", String(Date.now()));
    }
  };

  // Forget the recorded expiry (logout / session reset) so a later session never inherits it.
  const clearExpiry = () => {
    state.value.expiresAt = null;
    if (import.meta.client) {
      try {
        localStorage.removeItem("is_authenticated_exp");
        localStorage.setItem("is_authenticated_updated_at", String(Date.now()));
      } catch {}
    }
  };

  const setIsAuthenticated = (expiresInSeconds: number) => {
 
    const cookie = useCookie<string | null>("isAuthenticated");
    // if (cookie.value) 
      state.value.isAuthenticated = cookie.value;
    saveExpiry(expiresInSeconds);
  };

  const performRefresh = async () => {
    if (state.value.refreshing) {
      return;
    }
    state.value.refreshing = true;
    try {
      const cookie = useCookie<string | null>("isAuthenticated");
      if (cookie.value) {
        const result = await getNewToken("use-auth-refresh");
        if (result) {
          setIsAuthenticated(ACCESS_TOKEN_EXPIRY_SECONDS);
        } else {
          // Refresh failed - clear auth state
          state.value.isAuthenticated = null;
          state.value.expiresAt = null;
          cookie.value = null;
        }
      }
    } finally {
      state.value.refreshing = false;
    }
  };

  const ensureFresh = async () => {
    if (isExpiringSoon()) {
      await performRefresh();
    }
  };

  const isExpiringSoon = () => isTokenExpiringSoon(state.value.expiresAt, Date.now());

  // Background silent refresh timer (client only)
  if (import.meta.client) {
    let running = false;

    const { pause, resume, isActive } = useIntervalFn(
      async () => {
        if (!state.value.isAuthenticated || running) return;
        running = true;
        try {
          if (isExpiringSoon()) await performRefresh();
        } catch (err) {
          console.error("Auth refresh tick failed:", err);
        } finally {
          running = false;
        }
      },
      60_000,
      { immediate: false }
    );

    // Cross-tab sync (fires only for localStorage changes from other tabs)
    useEventListener(window, "storage", (e: StorageEvent) => {
      if (e.key === "is_authenticated_updated_at") {
        const cookie = useCookie<string | null>("isAuthenticated");
        if (cookie.value) state.value.isAuthenticated = cookie.value;
        const exp = localStorage.getItem("is_authenticated_exp");
        state.value.expiresAt = exp ? Number(exp) : null;
      }
    });

    // Visibility → ensure freshness (and optionally pause/resume polling)
    const visibility = useDocumentVisibility();
    watch(visibility, (v) => {
      if (v === "visible") ensureFresh();
      if (v === "hidden" && isActive.value) pause();
      if (v === "visible" && !isActive.value) resume();
    });

    onUnmounted(() => pause());
  }

  // Load initial access token from cookie on client mount
  const loadFromCookie = () => {
    if (import.meta.server) return;
    const cookie = useCookie<string | null>("isAuthenticated");
    if (cookie.value) {
      state.value.isAuthenticated = cookie.value;
      // If your API returns exp in JWT, you can decode it; here we fallback to 1h
      // Better: store `exp` in another cookie during login/refresh.
      const defaultExp = Date.now() + 60 * 60 * 1000;
      state.value.expiresAt =
        Number(localStorage.getItem("is_authenticated_exp")) || defaultExp;
    }
  };

  onMounted(loadFromCookie);

  return {
    setIsAuthenticated,
    clearExpiry,
    // isAuthenticated: computed(() => state.value.isAuthenticated),
    // expiresAt: computed(() => state.value.expiresAt),
    // refreshing: computed(() => state.value.refreshing),
    // refresh,
    // ensureFresh,
  };
}
